import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import {
  finalize,
  firstValueFrom,
  Observable,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import { LookupItem } from '../../../../core/models/lookup.models';
import { CommonService } from '../../../../shared/services/common.service';
import {
  ApiResponses,
  EditCaseDrawerParams,
  CaseDetailModel,
  CreatePreBipModel,
} from '../../insights.interface';
import { InsightsService } from '../../insights.service';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { CommonModule } from '@angular/common';
import { BehaviourSubListModel } from '../../../../core/models/common.models';
import { DrawerService } from '../../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import { differenceInCalendarDays, format } from 'date-fns';
import { OrganizationService } from '../../../organizations/organization.service';
import { LoaderService } from '../../../../core/services/loader.service';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { InsightMode } from '../../insights.enums';
import {
  OrgSchedules,
  ScheduleTemplate,
} from '../../../manage-settings/manage-settings.interface';
import { CustomTimeService } from '../../../manage-settings/custom-time/custom-time.service';
import {
  OrganizationDetailModel,
  userDetailModel,
} from '../../../organizations/organization.interface';
import { AuthService } from '../../../../core/services/auth.service';
import { LabelKey } from '../../../../core/models/report-labels.model';
import { ReportLabelsService } from '../../../manage-settings/report-labels/report-labels.service';
import { ReportLabelPipe } from '../../../../core/pipes/report-label.pipe';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { AuditContextService } from '../../../audit-logs/audit-context.service';

@Component({
  selector: 'app-create-pre-bip-case',
  standalone: true,
  imports: [
    CommonModule,
    NzFormModule,
    NzFormItemComponent,
    NzFormItemComponent,
    NzFormControlComponent,
    NzInputModule,
    NzIconModule,
    NzSelectModule,
    NzDatePickerModule,
    NzButtonModule,
    SvgIconComponent,
    NzRadioModule,
    ReactiveFormsModule,
    FormsModule,
    NzAutocompleteModule,
    NzIconModule,
    NzSpinModule,
    ReportLabelPipe,
    NzModalModule,
  ],
  templateUrl: './create-pre-bip-case.component.html',
})
export class CreatePreBipCaseComponent implements OnInit, OnDestroy {
  preBipForm: FormGroup;
  genderList: LookupItem[] = [];
  raceList: LookupItem[] = [];
  specialEducationList: LookupItem[] = [];
  languageLevelList: LookupItem[] = [];
  organizationList: LookupItem[] = [];
  generalEducationList: LookupItem[] = [];
  behaviourList: LookupItem[] = [];
  customScheduleList: ScheduleTemplate[] = [];
  behaviourSubTypeList: BehaviourSubListModel[] = [];
  selectedOrg: number | null = null;
  caseParams: EditCaseDrawerParams = {
    case: {} as CaseDetailModel,
    mode: InsightMode.CREATE,
  };
  caseData: CreatePreBipModel | null = null;

  userData: userDetailModel | null = null;
  loading$: Observable<boolean>;
  loading = false;
  today = new Date();
  orgData: OrganizationDetailModel | null = null;
  orgId: number | null = null;
  disabledDate = (current: Date): boolean =>
    differenceInCalendarDays(current, this.today) > 0;

  isOrganizationSearchLoading = false;
  isBehaviourSubTypeLoading = false;
  isScheduleLoading = false;
  needSafetyPlanLabel: string = LabelKey.NEEDS_SAFETY_PLAN;
  isTrainingWarningVisible = false;
  previousTrainingValue = false;
  IsIncludeDefaultSchedule = true;
  formSubmitted = false;
  private readonly destroy$ = new Subject<void>();
  constructor(
    private readonly insightsService: InsightsService,
    private readonly commonService: CommonService,
    private readonly drawerService: DrawerService,
    private readonly orgService: OrganizationService,
    private readonly loaderService: LoaderService,
    private readonly scheduleService: CustomTimeService,
    private readonly authService: AuthService,
    private readonly reportLabelsService: ReportLabelsService,
    private readonly auditContextService: AuditContextService
  ) {
    this.reportLabelsService
      .getLabelValue(LabelKey.NEEDS_SAFETY_PLAN)
      .subscribe(value => {
        this.needSafetyPlanLabel = value;
      });
    this.authService.orgData$.subscribe(res => {
      this.orgData = res;
      this.orgId = this.orgData?.organizationId || 0;
    });
    this.authService.userData$.subscribe(res => {
      this.userData = res;
    });
    this.preBipForm = this.insightsService.bipForm(this.caseData ?? null, [
      'bipTemplateId',
    ]);
    this.setupDrawerSubscription();
    this.loading$ = this.loaderService.loading$;
  }

  ngOnInit(): void {
    this.preBipForm = this.insightsService.bipForm(this.caseData ?? null, [
      'bipTemplateId',
    ]);
    this.insightsService.bindStudentIdExistsValidation(
      this.preBipForm.get('studentId'),
      this.destroy$,
      this.isEditMode
    );
    this.getMasterData();
    this.preBipForm.controls['caseBehaviorId'].valueChanges.subscribe(
      (val: number) => {
        if (!this.formSubmitted) {
          this.preBipForm.controls['caseBehaviorsType'].setValue(null);
          this.preBipForm.controls['caseBehaviorsType'].disable();
          this.behaviourSubTypeList = [];
          if (val) {
            this.getBehaviourSubTypes(val);
          }
        }
      }
    );
    this.preBipForm.controls['organizationId'].valueChanges.subscribe(
      (val: string) => {
        if (val) {
          this.preBipForm.controls['customScheduleId'].enable();
        } else {
          this.preBipForm.controls['customScheduleId'].setValue(null);
          this.preBipForm.controls['customScheduleId'].disable();
        }
        if (val && Number(val) !== this.selectedOrg) {
          this.selectedOrg = Number(val);
          this.customScheduleList = [];
          this.getCustomSchedules(Number(val));
        }
      }
    );
    if (this.isEditMode) {
      [
        'studentId',
        'organizationId',
        'firstName',
        'lastName',
        'genderId',
        'dateOfBirth',
        'raceId',
      ].forEach(ctrl => {
        this.preBipForm.controls[ctrl].enable();
      });
    }
  }

  get isEditMode(): boolean {
    return this.caseParams.mode === InsightMode.EDIT;
  }
  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<EditCaseDrawerParams>(DRAWER_IDS.PREBIP)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.caseParams = params;
          if (this.caseParams) {
            this.caseData = {
              dateOfBirth: this.caseParams?.case?.student.dateOfBirth || '',
              studentId: this.caseParams?.case?.student.enrollId || '',
              firstName: this.caseParams?.case?.student.firstName || '',
              genderId: Number(this.caseParams?.case?.student.genderId),
              lastName: this.caseParams?.case?.student.lastName || '',
              customScheduleId: Number(
                this.caseParams?.case?.student?.customScheduleId
              ),
              caseOwnerId: 1,
              organizationId: Number(
                this.caseParams?.case?.student.organizationId
              ),
              raceId: Number(this.caseParams?.case?.student.raceId),
              generalEducations:
                this.caseParams?.case?.generalEducations.map(g => {
                  return g.generalEducationId;
                }) || [],
              isEnabledSafetyPlan:
                this.caseParams?.case?.isEnabledSafetyPlan || false,
              isTrainingStudent:
                this.caseParams?.case?.isTrainingStudent || false,
              languageLevelId: Number(this.caseParams?.case?.languageLevelId),
              note: this.caseParams?.case?.note || '',
              specialEducations:
                this.caseParams?.case?.specialEducations.map(g => {
                  return g.specialEducationId;
                }) || [],
              caseBehaviorId: Number(
                this.caseParams?.case?.caseBehaviors[0].behaviorCategoryId
              ),
              caseBehaviorsType:
                this.caseParams?.case?.caseBehaviors[0].behaviorCategoryTypes.map(
                  c => {
                    return c.behaviorTypeId;
                  }
                ) || [],
            };
            this.selectedOrg = this.caseData.organizationId;
            this.getBehaviourSubTypes(Number(this.caseData?.caseBehaviorId));
            this.getCustomSchedules(Number(this.caseData?.organizationId));
          }
        }
      });
  }

  async getMasterData() {
    this.loaderService.show();
    const orgPromise: Promise<ApiResponses<LookupItem> | undefined> = this
      .userData?.isSuperAdmin
      ? firstValueFrom(this.commonService.getDataByType('Organization', ''))
      : (this.getAllOrganization(),
        Promise.resolve(undefined as ApiResponses<LookupItem> | undefined));

    await Promise.all([
      firstValueFrom(this.commonService.getDataByType('gender')),
      firstValueFrom(this.commonService.getDataByType('races')),
      firstValueFrom(this.commonService.getDataByType('SpecialEducation')),
      firstValueFrom(this.commonService.getDataByType('generalEducation')),
      firstValueFrom(this.commonService.getDataByType('LanguageLevels')),
      orgPromise,
      firstValueFrom(this.commonService.getDataByType('BehaviorCategory')),
    ])
      .then(
        ([
          genderList,
          raceList,
          specialEducationList,
          generalEducationList,
          languageLevelList,
          organizationList,
          behaviourList,
        ]: [
          ApiResponses<LookupItem> | undefined,
          ApiResponses<LookupItem> | undefined,
          ApiResponses<LookupItem> | undefined,
          ApiResponses<LookupItem> | undefined,
          ApiResponses<LookupItem> | undefined,
          ApiResponses<LookupItem> | undefined,
          ApiResponses<LookupItem> | undefined,
        ]) => {
          this.genderList = genderList ? genderList.result : [];
          this.raceList = raceList ? raceList.result : [];
          this.specialEducationList = specialEducationList
            ? specialEducationList.result
            : [];
          this.generalEducationList = generalEducationList
            ? generalEducationList.result
            : [];
          this.languageLevelList = languageLevelList
            ? languageLevelList.result
            : [];
          this.organizationList = organizationList
            ? organizationList.result
            : [];
          const organizationId =
            this.preBipForm.controls['organizationId'].value ||
            this.caseData?.organizationId ||
            this.orgId;
          if (organizationId) {
            this.preBipForm.controls['organizationId'].setValue(
              organizationId,
              {
                emitEvent: false,
              }
            );
          }
          this.preBipForm.controls['organizationId'].enable();
          this.getCustomSchedules(Number(this.orgId));
          this.behaviourList = behaviourList ? behaviourList.result : [];
          if (this.caseData) {
            const isOrgAvailable = this.organizationList.some(
              org => org.id === this.caseData?.organizationId
            );
            if (!isOrgAvailable) {
              this.getMissingOrganization(this.caseData.organizationId);
            }
          }
        }
      )
      .catch(e => console.error(e))
      .finally(() => this.loaderService.hide());
  }

  getBehaviourSubTypes(behaviourId: number) {
    this.isBehaviourSubTypeLoading = true;
    this.commonService
      .getBehaviourSubTypes(behaviourId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.preBipForm.controls['caseBehaviorsType'].enable();
          this.isBehaviourSubTypeLoading = false;
        })
      )
      .subscribe({
        next: (res: ApiResponses<BehaviourSubListModel>) => {
          this.behaviourSubTypeList = res.result;
        },
        error: (error: Error) => {
          this.commonService.notify('error', error.message);
        },
      });
  }

  getCustomSchedules(orgId: number) {
    this.isScheduleLoading = true;
    this.scheduleService
      .getAssignedScheduleListByOrgId(orgId, this.IsIncludeDefaultSchedule)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.preBipForm.controls['customScheduleId'].enable();
          this.isScheduleLoading = false;
        })
      )
      .subscribe({
        next: (res: OrgSchedules[]) => {
          const orgCustomSchedules = res;
          const scheduleforSelectedOrg = orgCustomSchedules.find(
            schedule =>
              schedule.organizationId ===
              this.preBipForm.controls['organizationId'].value
          );
          this.customScheduleList =
            scheduleforSelectedOrg?.scheduleTemplates || [];
          if (scheduleforSelectedOrg) {
            const defaultSchedule =
              scheduleforSelectedOrg.scheduleTemplates.find(
                schedule => schedule.isDefault
              );
            if (defaultSchedule) {
              this.preBipForm.controls['customScheduleId'].setValue(
                defaultSchedule.customScheduleId
              );
            }
          }
        },
        error: (error: Error) => {
          this.commonService.notify('error', error.message);
        },
      });
  }

  getMissingOrganization(orgId: number) {
    this.orgService.getOrganization(orgId).subscribe(res => {
      if (!!res && !!res.id) {
        this.organizationList.push({
          id: res.id,
          value: res.organizationName,
        });
      }
    });
  }

  onSearchOrganization(value: string): void {
    this.isOrganizationSearchLoading = true;
    if (this.userData?.isSuperAdmin) {
      this.commonService
        .getDataByType('Organization', value)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => {
            this.isOrganizationSearchLoading = false;
          })
        )
        .subscribe(orgList => {
          this.organizationList = orgList.result;
        });
    } else {
      this.getAllOrganization();
      this.isOrganizationSearchLoading = false;
    }
  }

  getAllOrganization() {
    this.commonService.orgList$
      .pipe(takeUntil(this.destroy$))
      .subscribe(orgs => {
        this.organizationList = orgs;
      });
  }

  onSubmit() {
    this.formSubmitted = true;
    if (this.preBipForm.invalid) {
      for (const key of Object.keys(this.preBipForm.controls)) {
        this.preBipForm.controls[key].markAsDirty();
        this.preBipForm.controls[key].updateValueAndValidity();
      }
      this.formSubmitted = false;
      return;
    }
    this.loading = true;
    const form = this.preBipForm.getRawValue();
    const data = {
      ...form,
      dateOfBirth: format(
        this.preBipForm.controls['dateOfBirth'].value,
        'yyyy-MM-dd'
      ),
    };

    // Build modified fields for audit logging
    const isCreateMode =
      !this.caseParams.case || this.caseParams.mode === InsightMode.CREATE;

    let original: Partial<CreatePreBipModel> = {};
    if (!isCreateMode && this.caseData) {
      // Map array/object fields to arrays of IDs for comparison
      const mapToId = (
        arr: ({ id: number } | number)[] | undefined
      ): number[] =>
        Array.isArray(arr)
          ? arr.map(
              (x: { id: number } | number): number =>
                typeof x === 'object' && x !== null
                  ? (x as { id: number }).id
                  : (x as number) //NOSONAR
            )
          : [];

      original = {
        ...this.caseData,
        specialEducations: mapToId(
          this.caseData.specialEducations as ({ id: number } | number)[]
        ),
        generalEducations: mapToId(
          this.caseData.generalEducations as ({ id: number } | number)[]
        ),
        caseBehaviorsType: mapToId(
          this.caseData.caseBehaviorsType as ({ id: number } | number)[]
        ),
      };
    }

    const labelMap: Record<string, string> = {
      studentId: 'Student ID',
      firstName: 'First Name',
      lastName: 'Last Name',
      genderId: 'Gender',
      dateOfBirth: 'Date of Birth',
      raceId: 'Race',
      organizationId: 'Organization',
      customScheduleId: 'Custom Schedule',
      caseBehaviorId: 'Case Behavior',
      caseBehaviorsType: 'Behavior Types',
      specialEducations: 'Special Educations',
      generalEducations: 'General Educations',
      languageLevelId: 'Language Level',
      note: 'Note',
      isTrainingStudent: 'Training Student',
      isEnabledSafetyPlan: 'Safety Plan Enabled',
    };

    const sensitiveFields: string[] = [];
    const excludeFields = ['caseOwnerId'];

    // Helper to enrich a data object with display values for audit log comparison
    const enrichData = (src: Partial<CreatePreBipModel>) => ({
      ...src,
      genderName: this.genderList.find(g => g.id === src.genderId)?.value || '',
      raceName: this.raceList.find(r => r.id === src.raceId)?.value || '',
      organizationName:
        this.organizationList.find(o => o.id === src.organizationId)?.value ||
        '',
      customScheduleName:
        this.customScheduleList.find(
          s => s.customScheduleId === src.customScheduleId
        )?.templateName || '',
      caseBehaviorName:
        this.behaviourList.find(b => b.id === src.caseBehaviorId)?.value || '',
      languageLevelName:
        this.languageLevelList.find(l => l.id === src.languageLevelId)?.value ||
        '',
      caseBehaviorsTypeName: (src.caseBehaviorsType || [])
        .map(
          (id: number) =>
            this.behaviourSubTypeList.find(b => b.id === id)?.name || ''
        )
        .filter(Boolean)
        .join(', '),
      specialEducationsName: (src.specialEducations || [])
        .map(
          (id: number) =>
            this.specialEducationList.find(s => s.id === id)?.value || ''
        )
        .filter(Boolean)
        .join(', '),
      generalEducationsName: (src.generalEducations || [])
        .map(
          (id: number) =>
            this.generalEducationList.find(g => g.id === id)?.value || ''
        )
        .filter(Boolean)
        .join(', '),
    });

    const enrichedData = enrichData(data);
    const originalEnriched = enrichData(original);

    const enrichedLabelMap = {
      ...labelMap,
      genderName: 'Gender',
      raceName: 'Race',
      organizationName: 'Organization',
      customScheduleName: 'Custom Schedule',
      caseBehaviorName: 'Case Behavior',
      languageLevelName: 'Language Level',
      caseBehaviorsTypeName: 'Behavior Types',
      specialEducationsName: 'Special Educations',
      generalEducationsName: 'General Educations',
    };

    const enrichedExcludeFields = [
      ...excludeFields,
      'genderId',
      'raceId',
      'organizationId',
      'customScheduleId',
      'caseBehaviorId',
      'languageLevelId',
      'caseBehaviorsType',
      'specialEducations',
      'generalEducations',
    ];

    // Only log changed fields, with correct old/new values
    const _modifiedFields = this.auditContextService.buildFieldChanges(
      originalEnriched,
      enrichedData,
      {
        sensitiveFields,
        labelMap: enrichedLabelMap,
        excludeFields: enrichedExcludeFields,
      }
    );

    const payload = { ...data, _modifiedFields };

    const operation$ =
      !!this.caseParams.case && this.caseParams.mode === InsightMode.EDIT
        ? this.insightsService.editPreBipCase(
            payload,
            Number(this?.caseParams?.case?.id)
          )
        : this.insightsService.createPreBipCase(payload);

    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: () => {
          this.handleSubmissionSuccess();
        },
        error: (error: Error) => {
          this.handleError(error);
        },
      });
  }

  private handleSubmissionSuccess(): void {
    this.showNotification();
    this.preBipForm.reset();
    this.closeDrawer();
  }

  private showNotification(): void {
    this.commonService.notify(
      'success',
      this.caseParams.mode === InsightMode.EDIT
        ? 'New Incident tracker has been updated successfully'
        : 'New Incident tracker has been created successfully',
      '',
      '450px'
    );
  }

  private handleError(error: Error): void {
    this.loading = false;

    this.commonService.notify('error', error.message);
  }

  closeDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.PREBIP_DETAIL);
    this.drawerService.closeDrawer(DRAWER_IDS.PREBIP);
  }

  onTrainingStudentChange(value: boolean | string | null): void {
    if (typeof value !== 'boolean') return;

    if (value === true && this.previousTrainingValue !== value) {
      this.previousTrainingValue =
        this.preBipForm.get('isTrainingStudent')?.value;
      this.isTrainingWarningVisible = true;
    }
  }

  handleCancelTrainingWarning(): void {
    this.isTrainingWarningVisible = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
